<template>
  <div class="batch-query-export">
    <div class="batch-history-section">
      <h3 class="section-title">批量查询历史记录</h3>
      <el-table :data="batchHistory" style="width: 100%" border>
        <el-table-column prop="create_time" label="创建时间" width="200"></el-table-column>
        <el-table-column prop="task_name" label="任务名称" min-width="200"></el-table-column>
        <el-table-column prop="task_status" label="任务状态" width="150">
          <template #default="scope">
            <el-tag :type="getStatusType(scope.row.task_status)">
              {{ getStatusText(scope.row.task_status) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="250" fixed="right">
          <template #default="scope">
            <el-button 
              type="danger" 
              size="small" 
              @click="handleTerminate(scope.row.id)"
              :disabled="!canTerminate(scope.row.task_status)"
            >
              终止
            </el-button>
            <el-button 
              type="warning" 
              size="small" 
              @click="handleComplete(scope.row.id)"
              :disabled="!canComplete(scope.row.task_status)"
            >
              补全
            </el-button>
            <el-button 
              type="primary" 
              size="small" 
              @click="handleExport(scope.row.id)"
              :disabled="!canExport(scope.row.task_status)"
              :title="getExportTooltip(scope.row.task_status)"
            >
              导出
            </el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { postWlApi } from '../utils/api_v1.js'

const exportLoading = ref(false)
const batchHistoryLoading = ref(false)

// 批量查询历史记录数据
const batchHistory = ref([])

// 获取批量查询历史记录
const fetchBatchHistory = async () => {
  batchHistoryLoading.value = true
  try {
    const response = await postWlApi('/batch_task/history/', {})
    
    if (response.code === 0) {
      batchHistory.value = response.data.records
    } else {
      ElMessage.error(`获取历史记录失败：${response.message}`)
    }
  } catch (error) {
    console.error('获取历史记录出错:', error)
    ElMessage.error('获取历史记录失败，请重试')
  } finally {
    batchHistoryLoading.value = false
  }
}

// 状态文本映射
const statusTextMap = {
  'wait_to_do': '待处理',
  'failed': '生成任务失败',
  'running': '运行中',
  'interrupted': '已中断',
  'part_failed': '部分失败',
  'perfect_done': '完全成功'
}

// 状态类型映射
const statusTypeMap = {
  'wait_to_do': 'info',
  'failed': 'danger',
  'running': 'warning',
  'interrupted': 'danger',
  'part_failed': 'warning',
  'perfect_done': 'success'
}

// 获取状态文本
const getStatusText = (status) => {
  return statusTextMap[status] || status
}

// 获取状态类型
const getStatusType = (status) => {
  return statusTypeMap[status] || 'info'
}

// 检查是否可以终止任务
const canTerminate = (status) => {
  return status === 'running'
}

// 检查是否可以补全任务
const canComplete = (status) => {
  return status === 'part_failed' || status === 'interrupted'
}

// 检查是否可以导出任务
const canExport = (status) => {
  return status === 'perfect_done' || status === 'part_failed' || status === 'running' || status === 'interrupted'
}

// 获取导出按钮的tooltip提示
const getExportTooltip = (status) => {
  if (status === 'running') {
    return '查询未完成，数据不全'
  }
  return ''
} 

// 终止任务
const handleTerminate = async (taskId) => {
  try {
    const response = await postWlApi('/batch_task/terminate/', { task_id: taskId })
    
    if (response.code === 0) {
      ElMessage.success('任务终止成功')
      fetchBatchHistory() // 刷新历史记录
    } else {
      ElMessage.error(`任务终止失败：${response.message}`)
    }
  } catch (error) {
    console.error('终止任务出错:', error)
    ElMessage.error('任务终止失败，请重试')
  }
}

// 补全任务
const handleComplete = async (taskId) => {
  try {
    const response = await postWlApi('/batch_task/fill/', { task_id: taskId })
    
    if (response.code === 0) {
      ElMessage.success('开始任务补全')
      fetchBatchHistory() // 刷新历史记录
    } else {
      ElMessage.error(`任务补全失败：${response.message}`)
    }
  } catch (error) {
    console.error('补全任务出错:', error)
    ElMessage.error('任务补全失败，请重试')
  }
}

// 导出任务
const handleExport = async (taskId) => {
  exportLoading.value = true
  
  try {
    const response = await postWlApi('/batch_task/excel_export/', {
      task_id: taskId
    }, {
      responseType: 'blob' // 设置响应类型为blob
    })
    
    // 检查响应结构
    const blobData = response.data
    
    if (blobData instanceof Blob) {
      // 从响应头中提取文件名
      let filename = `批量查询导出结果_${taskId}.xlsx`
      const contentDisposition = response.headers?.['content-disposition'] || response.headers?.get?.('content-disposition')
      
      if (contentDisposition) {
        // 处理编码后的Content-Disposition头
        if (contentDisposition.startsWith('=?utf-8?b?')) {
          // base64编码格式
          const base64Part = contentDisposition.replace('=?utf-8?b?', '').replace('?=', '')
          const decoded = decodeURIComponent(atob(base64Part).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join(''))
          
          // 从解码后的字符串中提取filename
          const filenameMatch = decoded.match(/filename="?([^"\s]+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1]
          }
        } else {
          // 普通格式
          const filenameMatch = contentDisposition.match(/filename="?([^"\s]+)"?/)
          if (filenameMatch && filenameMatch[1]) {
            filename = decodeURIComponent(filenameMatch[1])
          }
        }
      }
      
      // 创建下载链接
      const url = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      
      // 清理
      window.URL.revokeObjectURL(url)
      document.body.removeChild(link)
      
      ElMessage.success('批量查询结果导出成功')
    } else {
      // 尝试解析错误信息
      try {
        const errorText = await new Promise((resolve, reject) => {
          const reader = new FileReader()
          reader.onload = () => resolve(reader.result)
          reader.onerror = reject
          reader.readAsText(blobData)
        })
        ElMessage.error(`导出失败：${errorText}`)
      } catch {
        ElMessage.error(`导出失败：${response.statusText || '未知错误'}`)
      }
    }
  } catch (error) {
    console.error('导出出错:', error)
    ElMessage.error('导出失败，请重试')
  } finally {
    exportLoading.value = false
  }
}

// 页面挂载时获取批量查询历史记录
onMounted(() => {
  fetchBatchHistory()
})
</script>

<style scoped>
.batch-query-export {
  padding: 20px 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 2px solid #409eff;
  padding-bottom: 8px;
  margin-top: 30px;
}

.section-title:first-child {
  margin-top: 0;
}

.batch-history-section {
  margin-bottom: 40px;
}

.export-form-section {
  margin-top: 40px;
}

.export-form {
  max-width: 800px;
}

.el-table {
  margin-top: 10px;
}

.el-button + .el-button {
  margin-left: 5px;
}
</style>